import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { cn } from '@/utils/cn'

interface VolumeChartProps {
  data: Array<{ hour: string; count: number }>
  title?: string
  className?: string
}

const CustomTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: Array<{ value: number }>
  label?: string
}) => {
  if (active && payload && payload.length > 0) {
    return (
      <div className="glass-card p-3 text-xs">
        <p className="text-[var(--text-muted)] mb-1">{label}</p>
        <p className="font-semibold text-[var(--text-primary)]">
          {payload[0].value} mensagens
        </p>
      </div>
    )
  }
  return null
}

export function VolumeChart({ data, title, className }: VolumeChartProps) {
  return (
    <div className={cn('glass-card p-6', className)}>
      {title && (
        <h3 className="text-base font-semibold text-[var(--text-primary)] mb-4">
          {title}
        </h3>
      )}
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={data} margin={{ top: 5, right: 20, left: -20, bottom: 5 }}>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="var(--border-subtle)"
            vertical={false}
          />
          <XAxis
            dataKey="hour"
            tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} />
          <Line
            type="monotone"
            dataKey="count"
            name="Mensagens"
            stroke="var(--color-brand)"
            strokeWidth={2.5}
            dot={false}
            activeDot={{ r: 5, fill: 'var(--color-brand)' }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

interface TypesChartProps {
  data: Record<string, number>
  title?: string
  className?: string
}

const typeLabels: Record<string, string> = {
  text: 'Texto',
  image: 'Imagem',
  video: 'Vídeo',
  audio: 'Áudio',
  document: 'Documento',
  sticker: 'Figurinha',
  other: 'Outros',
}

export function TypesChart({ data, title, className }: TypesChartProps) {
  const chartData = Object.entries(data).map(([key, value]) => ({
    name: typeLabels[key] ?? key,
    value,
  }))

  return (
    <div className={cn('glass-card p-6', className)}>
      {title && (
        <h3 className="text-base font-semibold text-[var(--text-primary)] mb-4">
          {title}
        </h3>
      )}
      <ResponsiveContainer width="100%" height={220}>
        <BarChart
          data={chartData}
          margin={{ top: 5, right: 20, left: -20, bottom: 5 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="var(--border-subtle)"
            horizontal={true}
            vertical={false}
          />
          <XAxis
            dataKey="name"
            tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            content={({ active, payload, label }) => {
              if (active && payload && payload.length > 0) {
                return (
                  <div className="glass-card p-3 text-xs">
                    <p className="text-[var(--text-muted)] mb-1">{label}</p>
                    <p className="font-semibold text-[var(--text-primary)]">
                      {payload[0].value} mensagens
                    </p>
                  </div>
                )
              }
              return null
            }}
          />
          <Bar
            dataKey="value"
            name="Mensagens"
            fill="var(--color-brand)"
            radius={[4, 4, 0, 0]}
            opacity={0.8}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

interface CampaignSendsChartProps {
  data: Array<{ hour: string; sent: number; failed: number }>
  title?: string
  className?: string
}

export function CampaignSendsChart({ data, title, className }: CampaignSendsChartProps) {
  return (
    <div className={cn('glass-card p-6', className)}>
      {title && (
        <h3 className="text-base font-semibold text-[var(--text-primary)] mb-4">
          {title}
        </h3>
      )}
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={data} margin={{ top: 5, right: 20, left: -20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" vertical={false} />
          <XAxis
            dataKey="hour"
            tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
          <Tooltip
            content={({ active, payload, label }) => {
              if (!active || !payload || payload.length === 0) return null
              const sent = payload.find((item) => item.dataKey === 'sent')?.value ?? 0
              const failed = payload.find((item) => item.dataKey === 'failed')?.value ?? 0
              return (
                <div className="glass-card p-3 text-xs">
                  <p className="text-[var(--text-muted)] mb-1">{label}</p>
                  <p className="font-semibold text-[var(--text-primary)]">Enviadas: {sent}</p>
                  <p className="font-semibold text-[var(--text-primary)]">Falhas: {failed}</p>
                </div>
              )
            }}
          />
          <Legend
            wrapperStyle={{ fontSize: 11, color: 'var(--text-muted)' }}
            iconType="circle"
          />
          <Line
            type="monotone"
            dataKey="sent"
            name="Enviadas"
            stroke="var(--color-info)"
            strokeWidth={2.5}
            dot={false}
            activeDot={{ r: 5, fill: 'var(--color-info)' }}
          />
          <Line
            type="monotone"
            dataKey="failed"
            name="Falhas"
            stroke="var(--color-error)"
            strokeWidth={2.5}
            dot={false}
            activeDot={{ r: 5, fill: 'var(--color-error)' }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}
